import { useEffect, useMemo, useState } from 'react';
import {
  Button, Field, MessageBar, MessageBarBody, MessageBarTitle, Select, Spinner, Text,
} from '@fluentui/react-components';
import { Checkmark20Regular, Copy20Regular } from '@fluentui/react-icons';
import type { PostgreSQLCDCDiscovery } from '../../api/types';
import {
  generatePostgreSQLCDCAdminScript, generatePostgreSQLCDCCleanupScript,
  POSTGRESQL_CDC_DOCKER_COMPOSE_COMMAND, POSTGRESQL_CDC_DOCKER_RUN_COMMAND,
  POSTGRESQL_CDC_PROVIDER_NAMES, postgreSQLCDCProviderDisableSteps, postgreSQLCDCProviderSteps,
  type PostgreSQLCDCGuideProvider,
} from './postgreSQLCDCSetup';

interface Props {
  discovery: PostgreSQLCDCDiscovery;
  verifying: boolean;
  verificationError: string | null;
  onVerify: () => void;
}

interface CopyableCodeBlockProps {
  content: string;
  label: string;
  copied: boolean;
  onCopy: () => void;
}

function CopyableCodeBlock({ content, label, copied, onCopy }: CopyableCodeBlockProps) {
  return (
    <div className="postgresql-cdc-code-block">
      <pre className="postgresql-cdc-script" tabIndex={0}><code>{content}</code></pre>
      <Button className="postgresql-cdc-copy-button" appearance="subtle" size="small"
        icon={copied ? <Checkmark20Regular /> : <Copy20Regular />}
        aria-label={copied ? `${label} copied` : `Copy ${label}`}
        title={copied ? 'Copied' : `Copy ${label}`} onClick={onCopy} />
    </div>
  );
}

export function PostgreSQLCDCSetupPanel({
  discovery, verifying, verificationError, onVerify,
}: Props) {
  const [copiedBlock, setCopiedBlock] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<PostgreSQLCDCGuideProvider>(discovery.provider);
  useEffect(() => setSelectedProvider(discovery.provider), [discovery.provider]);
  const script = useMemo(
    () => generatePostgreSQLCDCAdminScript(discovery, selectedProvider),
    [discovery, selectedProvider],
  );
  const cleanupScript = useMemo(() => generatePostgreSQLCDCCleanupScript(discovery), [discovery]);

  const copyCode = async (content: string, block: string) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedBlock(block);
    } catch {
      setCopiedBlock(null);
    }
  };

  const downloadScript = () => {
    const url = URL.createObjectURL(new Blob([script], { type: 'text/sql;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'postgresql-cdc-setup.sql';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className="postgresql-cdc-setup" aria-label="PostgreSQL CDC administrator setup">
      <div className="postgresql-cdc-status-row">
        <div className={`postgresql-cdc-status postgresql-cdc-status-${verifying ? 'verifying' : discovery.compatible ? 'enabled' : 'disabled'}`}
          role="status" aria-live="polite">
          {verifying && <Spinner size="tiny" />}
          <Text weight="semibold">
            {verifying ? 'Verifying CDC…' : discovery.compatible ? 'CDC enabled' : 'CDC not enabled'}
          </Text>
        </div>
        <Button appearance={discovery.compatible ? 'secondary' : 'primary'}
          disabled={verifying} onClick={onVerify}>
          {verifying ? 'Verifying…' : 'Verify configuration'}
        </Button>
      </div>
      <details className="postgresql-cdc-guidance">
        <summary>Setup guide</summary>
        <div className="postgresql-cdc-guidance-content">
          <Text size={200}>CDC captures committed inserts, updates, and deletes without relying on a timestamp column.</Text>
          <Text size={200}>
            PostgreSQL records changes in its Write-Ahead Log (WAL). Logical replication exposes those row changes to CDC
            and may require a server restart to enable.
          </Text>
          <Text size={200}>
            Logical replication increases WAL volume. Inactive or delayed replication slots retain WAL and can consume
            storage until they catch up or are removed. Monitor available storage and slot lag, and remove unused slots deliberately.
          </Text>
          <Text weight="semibold">Administrator setup</Text>
          <MessageBar intent="warning">
            <MessageBarBody>
              Enabling logical replication can require a PostgreSQL restart and increases WAL storage. A restart alone does not change the active configuration.
            </MessageBarBody>
          </MessageBar>
          <Field label="PostgreSQL environment" hint={`Detected: ${POSTGRESQL_CDC_PROVIDER_NAMES[discovery.provider]}`}>
            <Select value={selectedProvider}
              onChange={(_, data) => setSelectedProvider(data.value as PostgreSQLCDCGuideProvider)}>
              {Object.entries(POSTGRESQL_CDC_PROVIDER_NAMES).map(([value, label]) =>
                <option key={value} value={value}>{label}</option>)}
            </Select>
          </Field>
          <Text weight="semibold">Ask a {POSTGRESQL_CDC_PROVIDER_NAMES[selectedProvider]} administrator to</Text>
          <ol>
            {postgreSQLCDCProviderSteps(selectedProvider).map(step => <li key={step}>{step}</li>)}
            <li>Run the generated administrator SQL.</li>
            <li>Return here and select <strong>Verify configuration</strong>.</li>
          </ol>
          {selectedProvider === 'docker' && (
            <>
              <Text weight="semibold">Docker Run command</Text>
              <CopyableCodeBlock content={POSTGRESQL_CDC_DOCKER_RUN_COMMAND} label="Docker Run command"
                copied={copiedBlock === 'docker-run'}
                onCopy={() => void copyCode(POSTGRESQL_CDC_DOCKER_RUN_COMMAND, 'docker-run')} />
              <Text weight="semibold">Docker Compose command</Text>
              <CopyableCodeBlock content={POSTGRESQL_CDC_DOCKER_COMPOSE_COMMAND} label="Docker Compose command"
                copied={copiedBlock === 'docker-compose'}
                onCopy={() => void copyCode(POSTGRESQL_CDC_DOCKER_COMPOSE_COMMAND, 'docker-compose')} />
            </>
          )}
          <CopyableCodeBlock content={script} label="administrator SQL" copied={copiedBlock === 'setup-sql'}
            onCopy={() => void copyCode(script, 'setup-sql')} />
          <div className="postgresql-cdc-actions">
            <Button size="small" onClick={downloadScript}>Download .sql</Button>
          </div>
          <details className="postgresql-cdc-cleanup">
            <summary>Disable CDC and clean up</summary>
            <div className="postgresql-cdc-cleanup-content">
              <MessageBar intent="warning">
                <MessageBarBody>
                  First stop CDC ingestion and remove the connection. Dropping a slot can lose its unread changes;
                  disabling logical replication server-wide can disrupt other applications.
                </MessageBarBody>
              </MessageBar>
              <ol>
                <li>Stop CDC ingestion and remove this connection so it cannot recreate or use its resources.</li>
                <li>Run the cleanup SQL below as an administrator. It only targets resources recorded as application-created.</li>
                <li>Confirm no other logical replication consumers or slots remain.</li>
                {postgreSQLCDCProviderDisableSteps(selectedProvider).map(step => <li key={step}>{step}</li>)}
              </ol>
              <CopyableCodeBlock content={cleanupScript} label="cleanup SQL" copied={copiedBlock === 'cleanup-sql'}
                onCopy={() => void copyCode(cleanupScript, 'cleanup-sql')} />
              <Text size={200}>
                Replication role access is not revoked automatically because the role may be shared. Review it separately.
              </Text>
            </div>
          </details>
        </div>
      </details>
      {verificationError && (
        <MessageBar intent="error" layout="multiline" role="alert">
          <MessageBarBody>
            <MessageBarTitle>CDC verification failed</MessageBarTitle>
            {verificationError}
          </MessageBarBody>
        </MessageBar>
      )}
    </section>
  );
}